import { range } from "lodash";
import { FC } from "react";

import { CalendarDay } from "shared/components";
import { months } from "shared/constants";
import { TinyText } from "shared/styles";
import { IDate, IFormOption } from "shared/types";
import { dateToIso, getMonthInfo, getWeekdays, isoToDate } from "shared/utils";

import {
  EventBlocksType,
  IBirthdays,
  IEvent,
  IIconBandSetting,
} from "Bands/types";

import {
  StyledCalendar,
  StyledDays,
  StyledMonth,
  StyledWeekday,
  StyledWeekdays,
} from "./styles";

interface CalendarProps {
  today: Date;
  date: IDate;
  events?: IEvent[];
  bandCalendar?: boolean;
  canAddEvent?: boolean;
  bandId?: string | null;
  birthdays?: IBirthdays;
  setSelectedEventBlocks?: (events: IFormOption[]) => void;
  setSelectedSourceEvent?: (events: IEvent | null) => void;
  eventId?: string;
  blockType?: EventBlocksType;
  infoBlockType?: IIconBandSetting;
}

export const Calendar: FC<CalendarProps> = ({
  today,
  date,
  events,
  bandCalendar = true,
  canAddEvent,
  bandId,
  birthdays,
  setSelectedEventBlocks,
  setSelectedSourceEvent,
  eventId,
  blockType,
  infoBlockType,
}) => {
  const { monthStartsOn, daysInMonth } = getMonthInfo(date);
  const { daysInMonth: daysInPrevMonth } = getMonthInfo({
    ...date,
    month: date.month ? date.month - 1 : 11,
  });

  const prevDays = monthStartsOn;
  const nextDays = 7 - ((daysInMonth + prevDays) % 7);

  return (
    <StyledCalendar>
      <StyledMonth>
        <TinyText>
          {months[date.month]} {date.year}
        </TinyText>
      </StyledMonth>
      <StyledWeekdays>
        {getWeekdays().map((day) => (
          <StyledWeekday key={`weekday${day}`}>
            <TinyText>{day}</TinyText>
          </StyledWeekday>
        ))}
      </StyledWeekdays>
      <StyledDays>
        {/* previous month */}
        {prevDays > 0 &&
          range(daysInPrevMonth - prevDays, daysInPrevMonth).map((day) => (
            <CalendarDay
              key={`prev${day}`}
              isDisabled
              day={day + 1}
              setSelectedEventBlocks={setSelectedEventBlocks}
              setSelectedSourceEvent={setSelectedSourceEvent}
              blockType={blockType}
              infoBlockType={infoBlockType}
            />
          ))}
        {/* current month */}
        {range(daysInMonth).map((day) => (
          <CalendarDay
            key={`current${day}`}
            day={day + 1}
            date={dateToIso({ ...date, day: day + 1 })}
            isActive={
              today.getDate() === day + 1 &&
              today.getMonth() === date.month &&
              today.getFullYear() === date.year
            }
            events={events
              ?.filter(
                (event) => event.date === dateToIso({ ...date, day: day + 1 })
              )
              ?.filter((event) => {
                if (!eventId) return true;
                return event.id !== eventId;
              })}
            bandCalendar={bandCalendar}
            canAddEvent={canAddEvent}
            bandId={bandId}
            birthdays={birthdays?.[String(date.month)]?.filter((bday) => {
              const isoBday = isoToDate(bday.date);
              return isoBday.day === day + 1;
            })}
            setSelectedEventBlocks={setSelectedEventBlocks}
            setSelectedSourceEvent={setSelectedSourceEvent}
            blockType={blockType}
            infoBlockType={infoBlockType}
          />
        ))}
        {/* next month */}
        {nextDays < 7 &&
          range(0, nextDays).map((day) => (
            <CalendarDay key={`next${day}`} isDisabled day={day + 1} />
          ))}
      </StyledDays>
    </StyledCalendar>
  );
};
